'use client';

import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import type { ComponentProps } from 'react';
import { useLoadingNavigation } from './LoadingNavigation';

type Props = ComponentProps<typeof NextLink> & {
  loadingScope?: 'page' | 'calendar';
};

function TrackedLink({ loadingScope = 'page', onNavigate, ...props }: Props) {
  const router = useRouter();
  const navigation = useLoadingNavigation()!;
  return (
    <NextLink
      {...props}
      onNavigate={(event) => {
        let cancelled = false;
        onNavigate?.({
          preventDefault() {
            cancelled = true;
            event.preventDefault();
          },
        });
        if (cancelled) return;
        // All app links use string hrefs. Leave URL objects to Next's own handling.
        if (typeof props.href !== 'string') return;
        const target = new URL(props.href, window.location.href);
        if (
          target.pathname === window.location.pathname &&
          target.search === window.location.search
        )
          return;
        event.preventDefault();
        navigation.run(props.href, loadingScope, () => {
          const navigate = props.replace ? router.replace : router.push;
          navigate(props.href as string, { scroll: props.scroll });
        });
      }}
    />
  );
}

export default function LoadingLink({ loadingScope, ...props }: Props) {
  const navigation = useLoadingNavigation();
  if (navigation) return <TrackedLink loadingScope={loadingScope} {...props} />;
  return <NextLink {...props} />;
}
